import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Edit2, ExternalLink, Calendar, MapPin, Package, DollarSign, Plus } from 'lucide-react';
import { vendorApi } from '../services/api';
import { Vendor, VendorReservation, VendorUpdate } from '../types';
import { extractApiError } from '../utils/errorUtils';
import { confirmAsync } from '../utils/toast';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { formatDateRange } from '../utils/fairStatus'; // Issue #1672
interface VendorDetailsModalProps {
  vendorId: number;
  onClose: () => void;
}

export default function VendorDetailsModal({ vendorId, onClose }: VendorDetailsModalProps) {
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [reservations, setReservations] = useState<VendorReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [formData, setFormData] = useState<VendorUpdate>({});
  const [productTypeInput, setProductTypeInput] = useState('');

  // Helper function to initialize form data from vendor data
  const initializeFormData = (vendorData: Vendor): VendorUpdate => ({
    business_name: vendorData.business_name,
    description: vendorData.description || '',
    bio: vendorData.bio || '',
    website: vendorData.website || '',
    social_media_facebook: vendorData.social_media_facebook || '',
    social_media_instagram: vendorData.social_media_instagram || '',
    product_types: vendorData.product_types || [],
  });

  useEffect(() => {
    let isMounted = true;

    const loadVendorData = async () => {
      try {
        setLoading(true);
        const [vendorData, reservationData] = await Promise.all([
          vendorApi.adminGetVendor(vendorId),
          vendorApi.adminGetVendorReservations(vendorId)
        ]);

        if (isMounted) {
          setVendor(vendorData);
          setReservations(reservationData);
          setFormData(initializeFormData(vendorData));
          setError(null);
        }
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load vendor data:', apiError);
        if (isMounted) {
          setError(apiError.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadVendorData();

    return () => {
      isMounted = false;
    };
  }, [vendorId]);

  // Keyboard navigation - close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]); // Include editMode to use updated handleClose, handleClose intentionally omitted to avoid re-registering

  const validateForm = (): string | null => {
    // Business name validation
    if (!formData.business_name?.trim()) {
      return 'Business name is required';
    }
    if (formData.business_name.length > 255) {
      return 'Business name must be 255 characters or less';
    }

    // Description length validation
    if (formData.description && formData.description.length > 1000) {
      return 'Description must be 1000 characters or less';
    }

    // Bio length validation
    if (formData.bio && formData.bio.length > 2000) {
      return 'Bio must be 2000 characters or less';
    }

    // URL validation helper
    const isValidUrl = (url: string) => {
      if (!url) return true; // Empty is OK
      return url.startsWith('http://') || url.startsWith('https://');
    };

    // Validate URLs
    if (formData.website && !isValidUrl(formData.website)) {
      return 'Website URL must start with http:// or https://';
    }
    if (formData.social_media_facebook && !isValidUrl(formData.social_media_facebook)) {
      return 'Facebook URL must start with http:// or https://';
    }
    if (formData.social_media_instagram && !isValidUrl(formData.social_media_instagram)) {
      return 'Instagram URL must start with http:// or https://';
    }

    return null;
  };

  const handleSave = async () => {
    if (!vendor) return;

    // Validate form before saving
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await vendorApi.adminUpdateVendor(vendor.id, formData);
      // Reload data without causing component unmount
      const [vendorData, reservationData] = await Promise.all([
        vendorApi.adminGetVendor(vendorId),
        vendorApi.adminGetVendorReservations(vendorId)
      ]);
      setVendor(vendorData);
      setReservations(reservationData);
      setFormData(initializeFormData(vendorData));
      setEditMode(false);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to update vendor:', apiError);
      setError(apiError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!vendor) return;
    setFormData(initializeFormData(vendor));
    setEditMode(false);
  };

  const handleClose = async () => {
    if (editMode) {
      const confirmed = await confirmAsync('You have unsaved changes. Are you sure you want to close?', {
        description: 'Your changes will be lost if you close without saving.',
        confirmLabel: 'Close Without Saving',
        cancelLabel: 'Keep Editing',
      });

      if (confirmed) {
        setEditMode(false);
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleAddProductType = () => {
    const trimmed = productTypeInput.trim();
    if (trimmed && !formData.product_types?.includes(trimmed)) {
      setFormData({
        ...formData,
        product_types: [...(formData.product_types || []), trimmed]
      });
      setProductTypeInput('');
    }
  };

  const handleRemoveProductType = (type: string) => {
    setFormData({
      ...formData,
      product_types: formData.product_types?.filter(t => t !== type) || []
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      confirmed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-8 max-w-4xl w-full">
          <p className="text-center text-gray-500">Loading vendor details...</p>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-8 max-w-4xl w-full">
          <p className="text-center text-red-500">{error || 'Vendor not found'}</p>
          <button
            onClick={onClose}
            className="mt-4 w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">Vendor Details</h2>
            <p className="text-sm text-gray-500 mt-1">ID: {vendor.id}</p>
          </div>
          <div className="flex items-center gap-2">
            {!editMode && (
              <>
                <a
                  href={`/vendors/${vendor.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  aria-label="View public profile in new tab"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Public Profile
                </a>
                <Link
                  to={`/admin/vendors/${vendor.id}/products`}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  aria-label="Manage vendor products"
                >
                  <Package className="h-4 w-4" />
                  Manage Products
                </Link>
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center gap-2"
                  aria-label="Edit vendor profile"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </button>
              </>
            )}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600"
              aria-label="Close modal"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Status Badge */}
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              vendor.is_active
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {vendor.is_active ? 'Active' : 'Inactive'}
            </span>
            <span className="text-sm text-gray-500">
              Created: {new Date(vendor.created_at).toLocaleDateString()}
            </span>
          </div>

          {/* Admin Metadata */}
          <div className="bg-moonbone border border-teal-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-ink">User ID:</span>
              <span className="text-primary-dark font-mono">{vendor.user_id}</span>
              <span className="text-secondary text-xs">(Admin Only)</span>
            </div>
          </div>

          {/* Business Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Business Information</h3>

            {editMode ? (
              <>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="edit-business-name" className="block text-sm font-medium text-gray-700">
                      Business Name *
                    </label>
                    <span className={`text-xs ${(formData.business_name?.length || 0) > 255 ? 'text-red-600' : 'text-gray-500'}`}>
                      {formData.business_name?.length || 0}/255
                    </span>
                  </div>
                  <input
                    id="edit-business-name"
                    data-testid="edit-vendor-business-name"
                    type="text"
                    value={formData.business_name || ''}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                    maxLength={255}
                    aria-label="Business Name *"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <span className={`text-xs ${(formData.description?.length || 0) > 1000 ? 'text-red-600' : 'text-gray-500'}`}>
                      {formData.description?.length || 0}/1000
                    </span>
                  </div>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    maxLength={1000}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Bio
                    </label>
                    <span className={`text-xs ${(formData.bio?.length || 0) > 2000 ? 'text-red-600' : 'text-gray-500'}`}>
                      {formData.bio?.length || 0}/2000
                    </span>
                  </div>
                  <textarea
                    value={formData.bio || ''}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    maxLength={2000}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-sm text-gray-600">Business Name</p>
                  <p className="text-base font-medium text-gray-900">{vendor.business_name}</p>
                </div>

                {vendor.description && (
                  <div>
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="text-base text-gray-900">{vendor.description}</p>
                  </div>
                )}

                {vendor.bio && (
                  <div>
                    <p className="text-sm text-gray-600">Bio</p>
                    <p className="text-base text-gray-900">{vendor.bio}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Product Types */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Product Types</h3>
            {editMode ? (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={productTypeInput}
                    onChange={(e) => setProductTypeInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddProductType())}
                    placeholder="Add product type"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleAddProductType}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>
                {formData.product_types && formData.product_types.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {formData.product_types.map((type, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                      >
                        {type}
                        <button
                          type="button"
                          onClick={() => handleRemoveProductType(type)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No product types specified</p>
                )}
              </div>
            ) : (
              <>
                {vendor.product_types && vendor.product_types.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {vendor.product_types.map((type, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No product types specified</p>
                )}
              </>
            )}
          </div>

          {/* Online Presence */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Online Presence</h3>

            {editMode ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website || ''}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Instagram
                  </label>
                  <input
                    type="url"
                    value={formData.social_media_instagram || ''}
                    onChange={(e) => setFormData({ ...formData, social_media_instagram: e.target.value })}
                    placeholder="https://instagram.com/username"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Facebook
                  </label>
                  <input
                    type="url"
                    value={formData.social_media_facebook || ''}
                    onChange={(e) => setFormData({ ...formData, social_media_facebook: e.target.value })}
                    placeholder="https://facebook.com/page"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                {vendor.website && (
                  <a
                    href={vendor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-secondary hover:text-primary-dark"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="text-sm">Website</span>
                  </a>
                )}
                {vendor.social_media_instagram && (
                  <a
                    href={vendor.social_media_instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-secondary hover:text-primary-dark"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="text-sm">Instagram</span>
                  </a>
                )}
                {vendor.social_media_facebook && (
                  <a
                    href={vendor.social_media_facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-secondary hover:text-primary-dark"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="text-sm">Facebook</span>
                  </a>
                )}
                {!vendor.website && !vendor.social_media_instagram && !vendor.social_media_facebook && (
                  <p className="text-sm text-gray-500">No online presence links</p>
                )}
              </div>
            )}
          </div>

          {/* Portfolio Images */}
          {vendor.portfolio_images && vendor.portfolio_images.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Portfolio</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {vendor.portfolio_images.map((imageUrl, idx) => (
                  <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-200">
                    <img
                      src={imageUrl}
                      alt={`Portfolio ${idx + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reservation History */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Reservation History ({reservations.length})
            </h3>
            {reservations.length > 0 ? (
              <div className="space-y-3">
                {reservations.map((reservation) => (
                  <div key={reservation.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{reservation.fair_name}</h4>
                        <p className="text-sm text-gray-500">Slot: {reservation.slot_number}</p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(reservation.status)}`}>
                        {reservation.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {formatDateRange(reservation.fair_start_date, reservation.fair_end_date)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        <span>{formatCurrency(reservation.price_paid)} {/* Issue #905: Decimal.js formatting */}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        <span>{reservation.reservation_type === 'full_duration' ? 'Full Duration' : 'Per Day'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span className="text-xs">
                          {reservation.fair_city}, {reservation.fair_state}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No reservations yet</p>
            )}
          </div>
        </div>

        {/* Footer with Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          {editMode ? (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={saving || !formData.business_name}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
